import { useCallback, useState } from "react";

export type SortDirection = "asc" | "desc";

export type SortState = {
  key: string;
  direction: SortDirection;
};

export type SortComparable = string | number | Date | boolean | null | undefined;

/** First click on a column starts ascending; same column toggles direction. */
export function toggleSort(
  current: SortState | null,
  key: string,
  initialDirection: SortDirection = "asc",
): SortState {
  if (current?.key === key) {
    return {
      key,
      direction: current.direction === "asc" ? "desc" : "asc",
    };
  }
  return { key, direction: initialDirection };
}

export function compareSortValues(a: SortComparable, b: SortComparable): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  if (typeof a === "boolean" && typeof b === "boolean") {
    return Number(a) - Number(b);
  }

  if (a instanceof Date || b instanceof Date) {
    const aTime = a instanceof Date ? a.getTime() : new Date(String(a)).getTime();
    const bTime = b instanceof Date ? b.getTime() : new Date(String(b)).getTime();
    if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
    if (Number.isNaN(aTime)) return 1;
    if (Number.isNaN(bTime)) return -1;
    return aTime - bTime;
  }

  if (typeof a === "number" && typeof b === "number") {
    if (Number.isNaN(a) && Number.isNaN(b)) return 0;
    if (Number.isNaN(a)) return 1;
    if (Number.isNaN(b)) return -1;
    return a - b;
  }

  const aStr = String(a);
  const bStr = String(b);
  const aNum = Number(aStr);
  const bNum = Number(bStr);
  if (
    aStr.trim() !== "" &&
    bStr.trim() !== "" &&
    Number.isFinite(aNum) &&
    Number.isFinite(bNum) &&
    /^-?\d+(\.\d+)?$/.test(aStr.trim()) &&
    /^-?\d+(\.\d+)?$/.test(bStr.trim())
  ) {
    return aNum - bNum;
  }

  return aStr.localeCompare(bStr, "de", { numeric: true, sensitivity: "base" });
}

export function sortRowsByKey<T>(
  rows: readonly T[],
  sort: SortState | null,
  getValue: (row: T, key: string) => SortComparable,
): T[] {
  if (!sort) return [...rows];
  const factor = sort.direction === "asc" ? 1 : -1;
  return [...rows].sort(
    (left, right) => factor * compareSortValues(getValue(left, sort.key), getValue(right, sort.key)),
  );
}

export function useTableSort(initial: SortState | null = null) {
  const [sort, setSort] = useState<SortState | null>(initial);

  const toggle = useCallback((key: string, initialDirection: SortDirection = "asc") => {
    setSort((current) => toggleSort(current, key, initialDirection));
  }, []);

  const reset = useCallback(() => {
    setSort(null);
  }, []);

  return { sort, setSort, toggle, reset };
}
